// Sistema de Scripts: alternativa a los function fields + triggers embebidos
// en `custom_fields` y `collection_function_triggers`. Una "script" es un
// artefacto autonomo (codigo JS + metadata) que se evalua contra el mismo
// sandbox de function fields (mismas defenses: whitelist, cross-tenant,
// MAX_QUERY_INVOCATIONS). Cada script puede:
//   - Dispararse en eventos de records (onCreate/onUpdate/onDelete) sobre
//     una coleccion objetivo.
//   - Dispararse manualmente desde la UI (evento `manual`).
//
// Persistencia:
//   - Tabla `scripts`: definicion + codigo + metadatos (nombre, descripcion,
//     eventos a los que responde, coleccion objetivo opcional).
//   - Tabla `script_run_logs` (definida en `logs.ts`): historial de runs.
//
// Permisos:
//   - Configurar scripts: modulo `custom_fields` (igual que collections).
//
// Design notes (2026-10-11):
//   - Esta tabla se creo despues de `collection_function_triggers`. Un
//     trigger embebido en una coleccion sigue siendo valido; un script
//     es la alternativa cuando quieres centralizar la logica fuera del
//     scope del collection_fields panel.
//   - El codigo del admin SI puede usar `db.*`, `records.*`, `dates.*`,
//     y opcionalmente `notify.*` (si el call site pasa notifyTransport).
//   - Sin embargo, los scripts NO pueden mutar la DB (no hay API de
//     escritura en el sandbox). Para side effects, usar notify.

import { v } from "convex/values";
import {
  internalControl,
  defineTableWithInternalControl,
} from "../../lib/shared/validators";

/** Eventos a los que un script puede responder. `manual` se dispara
 *  desde la UI con un boton "Probar". */
export const SCRIPT_EVENTS = [
  "onCreate",
  "onUpdate",
  "onDelete",
  "manual",
  /** Mensaje MQTT de `mqttConnectionId` que coincide con `mqttTopicFilter`. */
  "onMessage",
  /** Programado: cada N minutos o a una hora del dia (`schedule`). */
  "onSchedule",
] as const;

export type ScriptEvent = (typeof SCRIPT_EVENTS)[number];

export const scriptScheduleValidator = v.object({
  mode: v.union(
    v.literal("interval"),
    v.literal("daily"),
    v.literal("weekly"),
    v.literal("monthly"),
    v.literal("once"),
  ),
  intervalMinutes: v.optional(v.number()),
  /** "HH:mm" en la zona horaria del script. */
  time: v.optional(v.string()),
  /** 0 = domingo ... 6 = sabado. Diario: vacio = todos; semanal: al menos uno. */
  daysOfWeek: v.optional(v.array(v.number())),
  /** Mensual: 1-31, -1 = ultimo dia. */
  dayOfMonth: v.optional(v.number()),
  /** Una vez: "YYYY-MM-DD" en la zona del script. */
  date: v.optional(v.string()),
});

export const validatorScript = v.object({
  /** Nombre visible del script. */
  name: v.string(),
  /** Codigo JS del admin (max 5000 chars). */
  code: v.string(),
  /** Eventos a los que responde. Lista vacia = script deshabilitado. */
  events: v.array(
    v.union(
      v.literal("onCreate"),
      v.literal("onUpdate"),
      v.literal("onDelete"),
      v.literal("manual"),
      v.literal("onMessage"),
      v.literal("onSchedule"),
    ),
  ),
  /** Coleccion objetivo (opcional). Si no se setea, el script se ignora
   *  para eventos sobre records (solo responde a `manual`). */
  collectionId: v.optional(v.id("collections")),
  /** Tabla del sistema que dispara los eventos (`quotations`, `invoices`...).
   *  Excluyente con `collectionId`. Ver `convex/lib/scripting/registry.ts`. */
  targetTable: v.optional(v.string()),
  /** Conexion MQTT cuyos mensajes disparan `onMessage`. Excluyente con
   *  `collectionId` y `targetTable`. */
  mqttConnectionId: v.optional(v.id("integration_connections")),
  /** Filtro de topic para `onMessage` (`planta/+/temp`; vacio = `#`). */
  mqttTopicFilter: v.optional(v.string()),
  /** Programacion del evento `onSchedule` (ver `lib/scripting/schedule.ts`). */
  schedule: v.optional(scriptScheduleValidator),
  /** Proxima corrida programada (ms). Solo con `onSchedule` + `schedule` +
   *  `enabled`; si no, ausente (el cron no lo ve). */
  nextRunAt: v.optional(v.number()),
  /** Ultima corrida programada (ms). */
  lastScheduledRunAt: v.optional(v.number()),
  /** Trabajo del scheduler que dispara `nextRunAt` (ver scripts/scheduled.ts). */
  scheduledJobId: v.optional(v.id("_scheduled_functions")),
  /** Script de una accion de fila de coleccion: se administra desde la
   *  coleccion (no aparece en Configuracion -> Scripts) y no tiene eventos;
   *  solo corre cuando un usuario dispara la accion. */
  rowActionId: v.optional(v.id("collection_row_actions")),
  /** Si false, el script no se ejecuta aunque el evento coincida. */
  enabled: v.boolean(),
  /**
   * Timezone default que el sandbox pasara al service `dates`.
   * Ver `makeSandboxDb({ defaultTimezone })`.
   */
  defaultTimezone: v.optional(v.string()),
  internalControl,
});

export const scripts = defineTableWithInternalControl(validatorScript)
  // `by_company_id`, `by_company_and_state`, etc. ya vienen del helper.
  .index("by_company_and_enabled", [
    "internalControl.companyId",
    "enabled",
  ])
  .index("by_company_and_collection", [
    "internalControl.companyId",
    "collectionId",
  ])
  .index("by_company_and_target_table", [
    "internalControl.companyId",
    "targetTable",
  ])
  .index("by_company_and_mqtt_connection", [
    "internalControl.companyId",
    "mqttConnectionId",
  ])
  // Red de seguridad de scripts programados: los vencidos (`nextRunAt <= ahora`).
  .index("by_next_run", ["nextRunAt"]);
